import React, { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useGSAP } from '../hooks/useGSAP';
import SplitText from './SplitText';
import { gsap } from 'gsap';
import { usePreloaderComplete } from '../context/PreloaderContext';

const HeroSection = () => {
  const gsapHook = useGSAP();
  const pulseRef = useRef(null);
  const hand1Ref = useRef(null);
  const hand2Ref = useRef(null);
  const peekingManRef = useRef(null);
  // Single source of truth from App — no DOM polling, so the intro plays once.
  const preloaderComplete = usePreloaderComplete();

  useEffect(() => {
    if (pulseRef.current) {
      // Create pulsating animation with glow
      gsapHook.to(pulseRef.current, {
        scale: 1.3,
        opacity: 0.8,
        boxShadow: "0 0 20px rgba(34, 197, 94, 0.8), 0 0 40px rgba(34, 197, 94, 0.4)",
        duration: 1.5,
        ease: "power2.inOut",
        yoyo: true,
        repeat: -1
      });
    }
  }, [gsapHook]);

  // Animate hands and peeking man only after preloader is complete
  useEffect(() => {
    if (!preloaderComplete) return;

    const hand1 = hand1Ref.current;
    const hand2 = hand2Ref.current;
    const peekingMan = peekingManRef.current;

    // Create timeline for sequential animations
    const tl = gsap.timeline();

    // First: animate hands from bottom to top
    if (hand1) {
      gsap.set(hand1, { y: 200, opacity: 0 });
      tl.to(hand1, {
        y: 0,
        opacity: 1,
        duration: 1.2,
        ease: "power3.out"
      }, 0); // Start at same time
    }

    if (hand2) {
      gsap.set(hand2, { y: 200, opacity: 0 });
      tl.to(hand2, {
        y: 0,
        opacity: 1,
        duration: 1.2,
        ease: "power3.out"
      }, 0); // Start at same time
    }

    // After hands animation completes, animate peeking man from behind Usefull banner
    if (peekingMan) {
      gsap.set(peekingMan, {
        y: 250,
        opacity: 0,
        zIndex: -20 // Behind the Usefull banner
      });
      tl.to(peekingMan, {
        y: 0,
        opacity: 1,
        zIndex: 30, // Bring to front once revealed
        duration: 1.2,
        ease: "power3.out"
      }, "+=0.3"); // Start 0.3s after hands animation
    }

    // Kill the timeline on cleanup so a StrictMode re-run (dev) can't stack a
    // second copy of the intro on top of the first.
    return () => {
      tl.kill();
    };
  }, [preloaderComplete]);

  return (
    <div className="relative min-h-screen bg-[#f2f3d9]">
      {/* Main Content Container with responsive padding */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        {/* Responsive layout */}
        <div className="flex flex-col lg:flex-row lg:flex-wrap gap-6 lg:gap-[26px] items-start justify-start pt-32 sm:pt-36 lg:pt-[175px] pb-8 sm:pb-12 lg:pb-0">

          {/* Sander Kuyken / Info Section */}
          <div className="flex flex-col sm:flex-row items-start justify-start w-full lg:w-auto">
            <div className="flex flex-col gap-6 sm:gap-[29px] items-start justify-start w-full sm:w-[571px]">
              <div className="font-anton text-[80px] sm:text-[120px] md:text-[160px] lg:text-[200px] text-mainkleur leading-[0.89] tracking-[-2px] sm:tracking-[-4px] lg:tracking-[-8px] w-full sm:w-[545px]">
                {preloaderComplete && (
                  <>
                    <SplitText
                      text="SANDER"
                      className="mb-0 text-left"
                      splitType="words"
                      delay={250}
                      duration={2}
                      ease="power3.out"
                      from={{ opacity: 0, y: 40 }}
                      to={{ opacity: 1, y: 0 }}
                      threshold={0}
                      rootMargin={"+100px"}
                    />
                    <SplitText
                      text="KUYKEN"
                      className="text-left"
                      splitType="words"
                      delay={250}
                      duration={2.5}
                      initialDelay={800}
                      ease="power3.out"
                      from={{ opacity: 0, y: 40 }}
                      to={{ opacity: 1, y: 0 }}
                      threshold={0}
                      rootMargin={"+100px"}
                    />
                  </>
                )}
              </div>

              <div className="font-space font-normal text-[16px] sm:text-[20px] lg:text-[24px] text-[#262624] w-full lg:h-[161px]">
                {preloaderComplete && (
                  <SplitText
                    text={"4+ years of professional graphic-design experience, with the last 3 spent deep in web and UX. I pair creative direction with technical craft across gaming, tech, retail, real estate, beauty and more — always sweating the small details that make a product feel intentional."}
                    className="leading-[1.2] sm:leading-[0.907]"
                    splitType="lines"
                    delay={200}
                    duration={0.9}
                    ease="power3.out"
                    from={{ opacity: 0, y: 30 }}
                    to={{ opacity: 1, y: 0 }}
                    threshold={0}
                    rootMargin={"+100px"}
                  />
                )}
              </div>

              {/* Primary CTA pair (Fase 2): one to browse the work, one to start
                  a conversation. Renders on every breakpoint so mobile gets a
                  clear next step even though the illustration is hidden. */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full">
                <Link
                  to="/works"
                  className="group inline-flex items-center justify-center gap-2.5 bg-mainkleur text-licht rounded-full px-7 py-4 font-bricolage font-semibold text-base sm:text-lg uppercase tracking-tight hover:scale-[1.03] transition-transform duration-300"
                >
                  <span>View work</span>
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-0.5 transition-transform duration-300" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </Link>
                <Link
                  to="/contact"
                  className="group inline-flex items-center justify-center gap-2.5 border-2 border-mainkleur text-mainkleur rounded-full px-7 py-4 font-bricolage font-semibold text-base sm:text-lg uppercase tracking-tight hover:bg-mainkleur hover:text-licht transition-all duration-300"
                >
                  <span>Book a call</span>
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3M4 11h16M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </Link>
              </div>
            </div>

            {/* Social Icons - Mobile: horizontal, Desktop: vertical */}
            <div className="flex flex-row sm:flex-col gap-3 sm:gap-[13px] items-start justify-start w-auto sm:w-[44px] mt-6 sm:mt-0 sm:ml-[44px]">
              {/* LinkedIn */}
              <a href="https://www.linkedin.com/in/sander-kuyken-8a384716b/" target="_blank" rel="noopener noreferrer" className="border-2 border-mainkleur/50 rounded-[27px] w-[44px] h-[44px] flex items-center justify-center hover:bg-mainkleur hover:border-mainkleur transition-all duration-300 cursor-pointer">
                <svg className="w-[24px] h-[24px] text-black hover:text-white transition-colors duration-300" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" fill="currentColor">
                  <path d="M196.3 512L103.4 512L103.4 212.9L196.3 212.9L196.3 512zM149.8 172.1C120.1 172.1 96 147.5 96 117.8C96 103.5 101.7 89.9 111.8 79.8C121.9 69.7 135.6 64 149.8 64C164 64 177.7 69.7 187.8 79.8C197.9 89.9 203.6 103.6 203.6 117.8C203.6 147.5 179.5 172.1 149.8 172.1zM543.9 512L451.2 512L451.2 366.4C451.2 331.7 450.5 287.2 402.9 287.2C354.6 287.2 347.2 324.9 347.2 363.9L347.2 512L254.4 512L254.4 212.9L343.5 212.9L343.5 253.7L344.8 253.7C357.2 230.2 387.5 205.4 432.7 205.4C526.7 205.4 544 267.3 544 347.7L544 512L543.9 512z" />
                </svg>
              </a>

              {/* ArtStation */}
              <a href="https://www.artstation.com/kuyken" target="_blank" rel="noopener noreferrer" className="border-2 border-mainkleur/50 rounded-[27px] w-[44px] h-[44px] flex items-center justify-center hover:bg-mainkleur hover:border-mainkleur transition-all duration-300 cursor-pointer">
                <svg className="w-[24px] h-[24px] text-black hover:text-white transition-colors duration-300" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" fill="currentColor">
                  <path d="M66.1 441.4L109.1 515.7C113.4 524.2 119.9 531.4 128 536.4C136.1 541.4 145.4 544 155 544L440.4 544L381.2 441.4L66.1 441.4zM565.9 414L399.7 123.3C395.3 115.1 388.8 108.2 380.8 103.3C372.8 98.4 363.7 96 354.4 96L266 96L523.3 543.6L564 473.1C565.9 469.9 585 443.4 566 414zM339.2 368.5L223.7 168.5L108.2 368.5L339.2 368.5z" />
                </svg>
              </a>
            </div>
          </div>

          {/* Illustration - Hidden on mobile, visible on larger screens }
          <div className="relative hidden md:block h-[200px] md:h-[500px] lg:h-[669px] w-full md:w-[500px] lg:w-[633px] rounded-lg bg-transparent">
            {/* Soft accent halo behind the illustration — the hero's main pop of colour }
            <div aria-hidden="true" className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <div className="w-[82%] h-[68%] rounded-full bg-periwinkle/60 blur-[90px]" />
            </div>
            <img
              src="/images/illustration.png"
              alt="Illustration"
              className="relative z-10 w-full h-full object-contain bg-transparent shadow-lg top-[1018px] "
            />
          </div>
          */
         }
          {/* Graphic Designer Badge - Responsive positioning */}
          <div className="w-full sm:w-[255px] lg:ml-0 mt-2 lg:mt-2 self-end pb-[100px]">
            <div className="flex flex-col gap-6 sm:gap-[28px] items-start justify-start">
              <svg
                className="w-[20px] h-[20px] sm:w-[24.266px] sm:h-[24.704px]"
                xmlns="http://www.w3.org/2000/svg" width="26" height="27" viewBox="0 0 26 27" fill="none"
              >
                <line x1="0.713585" y1="1.29943" x2="24.3824" y2="25.4079" stroke="black" strokeWidth="2" />
                <path d="M24.2637 26.7041L24.2637 3.4751" stroke="black" strokeWidth="2" />
                <line x1="2.22852" y1="25.7041" x2="24.2663" y2="25.7041" stroke="black" strokeWidth="2" />
              </svg>

              <div className="font-bricolage font-medium text-[32px] sm:text-[40px] lg:text-[48px] text-black uppercase leading-[0.95]">
                <p className="mb-0">Graphic</p>
                <p>
                  <span className="inline-block bg-coral text-mainkleur px-2 -mx-1 rounded-md">designer</span>
                </p>
              </div>

              <div className="flex gap-3 sm:gap-[13px] items-center justify-start">
                <div className="w-[35px] h-[35px] border-2 border-green-500 bg-transparent flex justify-center items-center rounded-full">
                  <div
                    ref={pulseRef}
                    className="w-[15px] h-[15px] bg-green-500 rounded-full"
                  ></div>
                </div>
                <div className="font-space font-normal text-[14px] sm:text-[16px] text-black">
                  Available for work
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Hand Images - Hidden on mobile and tablet, visible on desktop above 1300px */}
      <img
        ref={hand1Ref}
        src="/images/hand2.png"
        alt="Hand illustration right"
        className="hero-hand hero-hand-right hidden xl:block absolute left-1/2 top-[1018px] transform translate-x-[92px] scale-x-[-1] w-[804px] h-[1500px] object-cover z-20"
        style={{ opacity: 0 }}
      />

      {/* Peeking man between hands - starts behind banner, comes forward after hands */}
      <img
        ref={peekingManRef}
        src="/images/hoofd.png"
        alt="Peeking man"
        className="hero-peeking-man hidden lg:block absolute left-[30%] top-[470px] translate-x-0 w-[720px] h-[720px] object-contain pointer-events-none"
        style={{ opacity: 0, zIndex: -20 }}
      />

      <img
        ref={hand2Ref}
        src="/images/hand.png"
        alt="Hand illustration left"
        className="hero-hand hero-hand-left hidden xl:block absolute left-[-166px] top-[1020px] w-[828px] h-[1500px] transform scale-x-[-1] object-cover z-20"
        style={{ opacity: 0 }}
      />
    </div>
  );
};

export default HeroSection;
